'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { Avatar, AvatarFallback } from '@extern/ui/avatar';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@extern/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@extern/ui/input-otp';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailboxIcon } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { string, z } from 'zod';
import { useSignUp } from '../_hooks/use-sign-up';
const FormSchema = z.object({
  code: string().min(6, {
    message: 'Please enter a valid code'
  })
});
export function SignUpEmailVerificationExperiment() {
  const $ = _c(35);
  const {
    onSignUpSubmitCode,
    onResendCode,
    isPending,
    isRedirecting,
    isResendDisabled,
    timeLeft,
    shouldRedirectToStart,
    emailAddress,
    goToStep
  } = useSignUp();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(FormSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const form = useForm(t0);
  let t1;
  let t2;
  if ($[1] !== goToStep || $[2] !== shouldRedirectToStart) {
    t1 = () => {
      if (shouldRedirectToStart) {
        goToStep("start");
      }
    };
    t2 = [shouldRedirectToStart, goToStep];
    $[1] = goToStep;
    $[2] = shouldRedirectToStart;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== onSignUpSubmitCode) {
    t3 = function handleSubmit(data) {
      onSignUpSubmitCode(data.code);
    };
    $[5] = onSignUpSubmitCode;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSubmit = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Avatar size="48"><AvatarFallback className="bg-role-info-container text-role-on-info-container" size="48" variant="icon"><MailboxIcon size={20} /></AvatarFallback></Avatar>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== emailAddress) {
    t5 = <div className="flex w-full flex-col items-center justify-center gap-6 text-center">{t4}<Text body="lg" className="w-full max-w-full text-role-on-background">To continue, enter the code sent to{" "}<span className="inline-block max-w-full break-all font-medium">{emailAddress}</span></Text></div>;
    $[8] = emailAddress;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== form || $[11] !== handleSubmit) {
    t6 = form.handleSubmit(handleSubmit);
    $[10] = form;
    $[11] = handleSubmit;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== form.control) {
    t7 = <FormField control={form.control} name="code" render={_temp} />;
    $[13] = form.control;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = isPending || isRedirecting || !form.formState.isValid;
  const t9 = isPending || isRedirecting;
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className="flex w-full gap-4"><Button className="flex-1" color="info" disabled={t8} isLoading={t9} type="submit">Continue</Button></div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6 || $[20] !== t7) {
    t11 = <form className="flex w-full flex-col gap-6" onSubmit={t6}>{t7}{t10}</form>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t7;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== form || $[23] !== t11) {
    t12 = <Form {...form}>{t11}</Form>;
    $[22] = form;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = isResendDisabled ? `${timeLeft}s` : "60s";
  let t14;
  if ($[25] !== t13) {
    t14 = <span className="text-role-info">Resend ({t13})</span>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== isResendDisabled || $[28] !== onResendCode || $[29] !== t14) {
    t15 = <Button appearance="ghost" className="gap-0" disabled={isResendDisabled} onClick={onResendCode} type="button">Didn't receive a code? {t14}</Button>;
    $[27] = isResendDisabled;
    $[28] = onResendCode;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t12 || $[32] !== t15 || $[33] !== t5) {
    t16 = <div className="flex w-full max-w-full flex-col items-center gap-6 overflow-clip rounded-3xl border border-role-outline-subtle bg-white p-6 sm:w-[358px]">{t5}{t12}{t15}</div>;
    $[31] = t12;
    $[32] = t15;
    $[33] = t5;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormControl><InputOTP data-cy="sign-up-email-otp" maxLength={6} {...field}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl><FormMessage /></FormItem>;
}